import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

/**
 * Owner/admin panel to manage a deployed app's USERS & VISIBILITY — LIVE, no
 * redeploy. The server reads these per request, so every change takes effect
 * immediately — unlike the deploy/Edit form, which rebuilds the app:
 *
 *   • Visibility — the Team-release flag (entry.ownerOnly): released to the
 *     team, or private to the owner. Toggled via PUT /owner-only.
 *   • Allowed users — named emails granted VIEW-only Open access even while the
 *     app is private/group-restricted; NO admin controls, NO RapidChat.
 *     Persisted under entry.allowedUsers (PUT /allowed-users).
 *   • Co-admins — full owner-level management parity (Edit, Stop, Redeploy,
 *     Delete, Email/Ask-Lyric API keys). Persisted under entry.coAdmins.
 *   • Testers   — the "🤖 RapidChat" live-tester overlay (record/replay + Ask AI
 *     + Report-a-bug) for this app, with NO management controls. Persisted under
 *     entry.testers.
 *
 * Props:
 *   appName     registry key of the app
 *   displayName friendly name (for the header)
 *   apiUrl      API base URL
 *   authFetch   (url, opts) => fetch, injects the Okta bearer token
 *   onClose     close the modal
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// One managed email list (co-admins OR testers). Renders the chips + add row and
// delegates persistence to the parent via onAdd/onRemove.
const EmailListSection = ({
  icon, title, blurb, accent, chipBg, list, owner, draft, setDraft,
  saving, onAdd, onRemove, placeholder,
}) => {
  const draftTrimmed = draft.trim().toLowerCase();
  const draftInvalid = draftTrimmed.length > 0 && !EMAIL_RE.test(draftTrimmed);
  const isOwnerEmail = !!owner && draftTrimmed === owner.toLowerCase();
  const isDup = list.some((e) => e.toLowerCase() === draftTrimmed);
  const disabled = !draftTrimmed || draftInvalid || isDup || isOwnerEmail || saving;

  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span aria-hidden="true" style={{ fontSize: 16 }}>{icon}</span>
        <h4 style={{ margin: 0, fontSize: 14, color: accent }}>{title}</h4>
      </div>
      <div style={{ color: '#aaa', fontSize: 12, marginBottom: 10, lineHeight: 1.5 }}>{blurb}</div>

      {list.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          {list.map((email) => (
            <span key={email} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px',
              borderRadius: 16, fontSize: 13, backgroundColor: chipBg,
              border: `1px solid ${accent}`, color: accent,
            }}>
              {email}
              <button
                type="button"
                onClick={() => onRemove(email)}
                disabled={saving}
                aria-label={`Remove ${email}`}
                style={{ background: 'none', border: 'none', color: accent, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 15, lineHeight: 1, padding: 0 }}
              >×</button>
            </span>
          ))}
        </div>
      ) : (
        <div style={{ color: '#666', fontSize: 13, fontStyle: 'italic', marginBottom: 12 }}>
          None yet.
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <input
          type="email"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); if (!disabled) onAdd(); } }}
          placeholder={placeholder}
          disabled={saving}
          maxLength={254}
          style={{
            flex: 1, padding: '8px 12px', borderRadius: 6, fontSize: 13,
            backgroundColor: '#222', color: '#e0e0e0',
            border: `1px solid ${draftInvalid ? '#ef5350' : '#444'}`,
          }}
        />
        <button
          type="button"
          onClick={onAdd}
          disabled={disabled}
          style={{
            padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600,
            border: 'none', color: '#111',
            cursor: disabled ? 'not-allowed' : 'pointer',
            backgroundColor: disabled ? '#555' : accent,
          }}
        >{saving ? 'Saving…' : 'Add'}</button>
      </div>

      {draftInvalid && <p style={{ color: '#ef5350', fontSize: 11, margin: '8px 0 0' }}>Enter a valid email address.</p>}
      {!draftInvalid && isOwnerEmail && <p style={{ color: '#888', fontSize: 11, margin: '8px 0 0' }}>That’s the owner — they already have access.</p>}
      {!draftInvalid && !isOwnerEmail && isDup && draftTrimmed && <p style={{ color: '#888', fontSize: 11, margin: '8px 0 0' }}>Already added.</p>}
    </div>
  );
};

const UsersModal = ({ appName, displayName, apiUrl, authFetch, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [owner, setOwner] = useState(null);
  const [coAdmins, setCoAdmins] = useState([]);
  const [testers, setTesters] = useState([]);
  const [allowedUsers, setAllowedUsers] = useState([]);
  const [ownerOnly, setOwnerOnly] = useState(false);
  const [coDraft, setCoDraft] = useState('');
  const [testerDraft, setTesterDraft] = useState('');
  const [allowedDraft, setAllowedDraft] = useState('');

  const coBase = `${apiUrl}/api/apps/${encodeURIComponent(appName)}/co-admins`;
  const testerBase = `${apiUrl}/api/apps/${encodeURIComponent(appName)}/testers`;
  const allowedBase = `${apiUrl}/api/apps/${encodeURIComponent(appName)}/allowed-users`;
  const ownerOnlyBase = `${apiUrl}/api/apps/${encodeURIComponent(appName)}/owner-only`;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [coRes, tRes, aRes] = await Promise.all([
        authFetch(coBase), authFetch(testerBase), authFetch(allowedBase),
      ]);
      const coData = await coRes.json().catch(() => ({}));
      if (!coRes.ok) throw new Error(coData.error || `HTTP ${coRes.status}`);
      setOwner(coData.owner || null);
      setCoAdmins(Array.isArray(coData.coAdmins) ? coData.coAdmins : []);
      const tData = await tRes.json().catch(() => ({}));
      if (!tRes.ok) throw new Error(tData.error || `HTTP ${tRes.status}`);
      setTesters(Array.isArray(tData.testers) ? tData.testers : []);
      const aData = await aRes.json().catch(() => ({}));
      if (!aRes.ok) throw new Error(aData.error || `HTTP ${aRes.status}`);
      setAllowedUsers(Array.isArray(aData.allowedUsers) ? aData.allowedUsers : []);
      setOwnerOnly(!!aData.ownerOnly);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, coBase, testerBase, allowedBase]);

  useEffect(() => { load(); }, [load]);

  // Close on Escape (matches the app-wide modal behavior).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  // Persist a new full list for one grant. Optimistically updates the UI and
  // reverts on failure so the displayed list always matches what's saved.
  //   kind: 'coAdmins' | 'testers' | 'allowedUsers'
  const persist = useCallback(async (kind, next) => {
    const map = {
      coAdmins: { base: coBase, setter: setCoAdmins, prev: coAdmins },
      testers: { base: testerBase, setter: setTesters, prev: testers },
      allowedUsers: { base: allowedBase, setter: setAllowedUsers, prev: allowedUsers },
    };
    const { base, setter, prev } = map[kind];
    setter(next);
    setSaving(true);
    setError(null);
    try {
      const res = await authFetch(base, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [kind]: next }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setter(Array.isArray(data[kind]) ? data[kind] : next);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch (err) {
      setter(prev);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }, [authFetch, coBase, testerBase, allowedBase, coAdmins, testers, allowedUsers]);

  // Flip the Team-release / visibility flag live (no redeploy). Optimistic with
  // revert-on-failure, same as the list grants.
  const setVisibility = useCallback(async (nextOwnerOnly) => {
    const prev = ownerOnly;
    setOwnerOnly(nextOwnerOnly);
    setSaving(true);
    setError(null);
    try {
      const res = await authFetch(ownerOnlyBase, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ownerOnly: nextOwnerOnly }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setOwnerOnly(!!data.ownerOnly);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch (err) {
      setOwnerOnly(prev);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }, [authFetch, ownerOnlyBase, ownerOnly]);

  const addCoAdmin = () => {
    const v = coDraft.trim().toLowerCase();
    if (!v || !EMAIL_RE.test(v) || coAdmins.some((e) => e.toLowerCase() === v) || v === (owner || '').toLowerCase()) return;
    persist('coAdmins', [...coAdmins, v]);
    setCoDraft('');
  };
  const addTester = () => {
    const v = testerDraft.trim().toLowerCase();
    if (!v || !EMAIL_RE.test(v) || testers.some((e) => e.toLowerCase() === v) || v === (owner || '').toLowerCase()) return;
    persist('testers', [...testers, v]);
    setTesterDraft('');
  };
  const addAllowed = () => {
    const v = allowedDraft.trim().toLowerCase();
    if (!v || !EMAIL_RE.test(v) || allowedUsers.some((e) => e.toLowerCase() === v) || v === (owner || '').toLowerCase()) return;
    persist('allowedUsers', [...allowedUsers, v]);
    setAllowedDraft('');
  };

  return ReactDOM.createPortal(
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 16,
        backgroundColor: 'rgba(0,0,0,0.6)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Users"
        style={{
          width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto',
          backgroundColor: '#14141f', border: '1px solid #333', borderRadius: 12,
          boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', borderBottom: '1px solid #2a2a3a', position: 'sticky', top: 0, backgroundColor: '#14141f', zIndex: 1 }}>
          <span aria-hidden="true" style={{ fontSize: 22 }}>👥</span>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 16, color: '#e0e0e0' }}>Users</h3>
            <div style={{ fontSize: 12, color: '#888' }}>{displayName || appName}</div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'none', border: 'none', color: '#888', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}
          >×</button>
        </div>

        <div style={{ padding: 20 }}>
          {loading ? (
            <div style={{ color: '#888', fontSize: 14 }}>Loading…</div>
          ) : (
            <>
              {owner && (
                <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
                  Owner: <span style={{ color: '#ccc' }}>{owner}</span>
                </div>
              )}

              {/* Visibility — Team release toggle (applies live, no redeploy) */}
              <div style={{
                marginBottom: 16, padding: 12, borderRadius: 8,
                backgroundColor: ownerOnly ? '#2a1f12' : '#12251a',
                border: `1px solid ${ownerOnly ? '#ffb74d' : '#2e7d32'}`,
              }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: saving ? 'not-allowed' : 'pointer', color: '#e0e0e0', fontSize: 14 }}>
                  <input
                    type="checkbox"
                    checked={!ownerOnly}
                    disabled={saving}
                    onChange={(e) => setVisibility(!e.target.checked)}
                    style={{ accentColor: '#66bb6a', marginTop: 3 }}
                  />
                  <span>
                    🚀 Released to my team
                    <span style={{ display: 'block', color: '#888', fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>
                      {ownerOnly
                        ? <><b style={{ color: '#ffb74d' }}>🔒 Private</b> — only you, co-admins, testers, and the allowed users below can open it.</>
                        : <>Everyone allowed by the app’s visibility setting can open it. Uncheck to make it <b style={{ color: '#ffb74d' }}>private</b>.</>}
                      <span style={{ color: '#66bb6a' }}> Applies instantly.</span>
                    </span>
                  </span>
                </label>
              </div>

              {/* Allowed users — view-only open access (matters when private/group-restricted) */}
              <EmailListSection
                icon="👤"
                title="Allowed users"
                accent="#64b5f6"
                chipBg="#12283e"
                blurb={<>Named people who can <b>Open</b> this app even while it’s <b style={{ color: '#ffb74d' }}>private</b> or group-restricted — <b>view-only</b>, no admin controls and no 🤖 RapidChat. <span style={{ color: '#66bb6a' }}>Applies instantly.</span></>}
                list={allowedUsers}
                owner={owner}
                draft={allowedDraft}
                setDraft={setAllowedDraft}
                saving={saving}
                onAdd={addAllowed}
                onRemove={(email) => persist('allowedUsers', allowedUsers.filter((x) => x !== email))}
                placeholder="user@example.com"
              />

              <div style={{ height: 1, backgroundColor: '#2a2a3a', margin: '4px 0 16px' }} />

              <EmailListSection
                icon="🛠️"
                title="Co-admins"
                accent="#ce93d8"
                chipBg="#2a1a3e"
                blurb={<>Full admin control (Edit, Stop, Redeploy, Delete, API keys) — plus every button, including 🤖 RapidChat. <span style={{ color: '#66bb6a' }}>Applies instantly.</span></>}
                list={coAdmins}
                owner={owner}
                draft={coDraft}
                setDraft={setCoDraft}
                saving={saving}
                onAdd={addCoAdmin}
                onRemove={(email) => persist('coAdmins', coAdmins.filter((x) => x !== email))}
                placeholder="admin@example.com"
              />

              <div style={{ height: 1, backgroundColor: '#2a2a3a', margin: '4px 0 16px' }} />

              <EmailListSection
                icon="🤖"
                title="Testers"
                accent="#b388ff"
                chipBg="#241a3a"
                blurb={<>Get the <b style={{ color: '#b388ff' }}>🤖 RapidChat</b> button (record/replay, Ask AI, Report&nbsp;a&nbsp;bug) for this app — <b>plus Open</b>, but no admin controls. Users not listed here don’t see RapidChat. <span style={{ color: '#66bb6a' }}>Applies instantly.</span></>}
                list={testers}
                owner={owner}
                draft={testerDraft}
                setDraft={setTesterDraft}
                saving={saving}
                onAdd={addTester}
                onRemove={(email) => persist('testers', testers.filter((x) => x !== email))}
                placeholder="tester@example.com"
              />

              {error && <p style={{ color: '#ef5350', fontSize: 12, margin: '12px 0 0' }}>{error}</p>}
              {savedFlash && !error && <p style={{ color: '#66bb6a', fontSize: 12, margin: '12px 0 0' }}>✓ Saved</p>}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default UsersModal;
